import { useEffect } from "react";

import { useQueries, useQueryClient } from "@tanstack/react-query";
// Components
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";
import GeneralContainer from "../GeneralContainer";
import SupportContainer from "../SupportContainer";
import PositionsContainer from "../PositionsContainer";
// Hooks
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
// Subscriptions
import { systemSettingsCb } from "@/Settings/helpers/SystemSettings/subscriptionCallbacks";
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { ISystemSettings } from "@/Settings/types/Common/model";
import { getAppendingConfigInfo, getSystemSettings, getSystemSettingsAdditionalInfo } from "@/Settings/api/SystemSettings/queries";
import { resolveOems } from "@/Settings/helpers/SystemSettings/appendingConfiguration";
import { useSystemSettingSubscription } from "@/Settings/hooks/useSystemSettingsSubscription";

const getContent = () => {
	return [
		<GeneralContainer key={1} />,
		<SupportContainer key={2} />,
		(selected: number) => <PositionsContainer key={3} selected={selected} />,
	];
};

const QUERY_IDENTIFIERS = [
	"systemSettings",
	"systemSettingsAdditionalInfo",
	"appendingConfigInfo",
	"getChargebackDeductionCodes",
	"PayPlansList",
	"getDepartmentHierarchy",
];

const SystemSettingsContainer = () => {
	const { clearCache } = useCacheCleaner();

	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId ?? "";
	const queryClient = useQueryClient();

	const oems = resolveOems();

	useQueries({
		queries: [
			{
				queryKey: ["systemSettings", locationId],
				queryFn: () => getSystemSettings(locationId, oems),
				enabled: !!locationId,
				staleTime: Infinity,
			},
			{
				queryKey: ["systemSettingsAdditionalInfo", locationId],
				queryFn: () => getSystemSettingsAdditionalInfo({ locationId }),
				enabled: !!locationId,
				staleTime: Infinity,
			},
			{
				queryKey: ["appendingConfigInfo", locationId],
				queryFn: () => getAppendingConfigInfo({ locationId, oems }),
				enabled: !!locationId && !!oems.length,
				staleTime: Infinity,
			},
		],
	});

	useEffect(() => {
		return () => {
			clearCache(QUERY_IDENTIFIERS);
		};
	}, [locationId]);

	//system setting subscription
	useSystemSettingSubscription(locationId, (response: GraphqlQueryResponse<"onUpdateSystemSettings", ISystemSettings>["data"]) => {
		systemSettingsCb({ response, queryClient, locationId });
	});

	return (
		<BodyLayer>
			<RoundedBox className="!p-0">
				<TabSelectable
					tabs={["General", "Support", "Positions"]}
					content={getContent()}
					tabsProps={{ style: { marginTop: "30px" } }}
					tabBarClassName="sticky top-[100px] z-[100] bg-white  rounded-lg"
					contentProps={{ minHeight: 550 }}
				/>
			</RoundedBox>
		</BodyLayer>
	);
};

export default SystemSettingsContainer;
